import { handleActions } from 'redux-actions'
import { selectorForSlice, setState, unsetState } from 'lp-redux-utils'
import * as actions from './actions'
import { setOnSuccess } from 'lp-redux-api'
import * as apiActions from 'api-actions'
import { set, unset, get } from 'lodash/fp'
import { compose } from 'redux'
import { concat, differenceBy, groupBy } from 'lodash'
import {
  buildUpdateParticipantsWithMappedAgreementHandler,
  planMatchResultsInitialState,
  resetPlanMatchResults,
  buildPlanMatchResultsActionHandlers,
  buildTransformedAudienceSelector,
} from '../products/reducerUtils'
import { AudienceType } from 'config/automatic-rollovers'

const reducerKey = 'automaticRollovers'
const slice = 'root.automaticRollovers'

const initialState = {
  participantsForSubmission: [],
  discardedParticipants: [],
  ...planMatchResultsInitialState,
}

const reducer = handleActions(
  {
    [actions.setLegaleseConfirmationForm]: setState(
      'legaleseConfirmationForm',
      (action, _, form) => ({ ...form, ...action.payload })
    ),
    [actions.setParticipantsForSubmission]: (state, action) => {
      return compose(
        set('participantsForSubmission', action.payload),
        unset('participantsAgreementHash'),
        resetPlanMatchResults
      )(state)
    },
    [actions.resetDiscardedParticipants]: setState('discardedParticipants'),
    [actions.setDiscardedParticipants]: (state, action) => {
      const { participants, reasonForDiscard } = action.payload
      // Add the reason for discard (used for generating the discarded participants csv file)
      const discardedParticipantsWithReason = participants.map(
        (participant) => ({
          ...participant,
          reasonForDiscard,
        })
      )

      // When setting discarded participants (e.g., for missing agreement or terminating DB),
      // participantsForSubmission selector needs to be updated as well since it's possible
      // for the user to go back to previous steps in the workflow and change their selections:
      // 1) Move participants discarded for the same reason to participantsSubmission selector
      // 2) Replace them with the participants from action.payload
      // 3) Remove the payload participants from participantsSubmission, using row as the unique ID

      const discardedParticipants = get('discardedParticipants', state) || []
      const participantsForSubmission =
        get('participantsForSubmission', state) || []

      const {
        true: participantsDiscardedForSameReason = [],
        false: participantsDiscardedForDifferentReason = [],
      } = groupBy(
        discardedParticipants,
        (participant) => participant.reasonForDiscard === reasonForDiscard
      )

      const updatedDiscardedParticipants = concat(
        participantsDiscardedForDifferentReason,
        discardedParticipantsWithReason
      )
      const updatedParticipantsForSubmission = differenceBy(
        concat(participantsForSubmission, participantsDiscardedForSameReason),
        participants,
        'row'
      )
      return compose(
        set('discardedParticipants', updatedDiscardedParticipants),
        set('participantsForSubmission', updatedParticipantsForSubmission)
      )(state)
    },
    [actions.setParticipantFileName]: setState('participantFileName'),
    [actions.setParticipantsAgreementHash]: setState(
      'participantsAgreementHash'
    ),
    [actions.updateParticipantsWithMappedAgreement]:
      buildUpdateParticipantsWithMappedAgreementHandler((agreement) => ({
        agreementType: agreement.portalAgreementType,
        recordType: agreement.recordType,
      })),
    [actions.clearParticipantsData]: (state) => {
      return compose(
        unset('legaleseConfirmationForm'),
        unset('participantFileName'),
        unset('participantsAgreementHash'),
        unset('planConfirmationAcknowledgementsForm'),
        set(
          'participantsForSubmission',
          initialState.participantsForSubmission
        ),
        set('discardedParticipants', initialState.discardedParticipants),
        resetPlanMatchResults
      )(state)
    },
    [actions.setCompleteServicesAgreementForm]: setState(
      'completeServicesAgreementForm',
      (action, _, form) => ({ ...form, ...action.payload })
    ),
    [actions.clearCompleteServicesAgreementForm]: unsetState(
      'completeServicesAgreementForm'
    ),
    [apiActions.fetchPlanSponsors]: setOnSuccess('planSponsors'),
    [apiActions.fetchRecordkeepers]: setOnSuccess('recordkeepers'),
    [apiActions.fetchTpas]: setOnSuccess('tpas'),
    [apiActions.fetchConsultants]: setOnSuccess('consultants'),
    [apiActions.fetchCompletedServicesAgreements]: setOnSuccess(
      'completedServicesAgreements'
    ),
    [actions.setPlanConfirmationAcknowledgementsForm]: setState(
      'planConfirmationAcknowledgementsForm',
      (action, _, form) => ({ ...form, ...action.payload })
    ),
    [apiActions.fetchErisapediaSearches]: setOnSuccess('erisapediaSearches'),
    [actions.resetErisapediaSearches]: unsetState('erisapediaSearches'),
    [actions.setRolloverType]: (state, action) =>
      compose(set('rolloverType', action.payload))(state),
    [actions.setAddAddendum]: (state, action) =>
      compose(set('addAddendum', action.payload))(state),
    [actions.setAddNotificationsAddAddendum]: (state, action) =>
      compose(set('addNotificationsAddendum', action.payload))(state),
    ...buildPlanMatchResultsActionHandlers(apiActions),
  },
  initialState
)

const select = selectorForSlice(slice)

const selectors = {
  planSponsors: select('planSponsors'),
  recordkeepers: select('recordkeepers'),
  tpas: select('tpas'),
  consultants: select('consultants'),
  legaleseConfirmationForm: select('legaleseConfirmationForm'),
  participantsForSubmission: select('participantsForSubmission'),
  discardedParticipants: select('discardedParticipants'),
  participantFileName: select('participantFileName'),
  participantsAgreementHash: select('participantsAgreementHash'),
  completedServicesAgreements: select('completedServicesAgreements'),
  completeServicesAgreementForm: select('completeServicesAgreementForm'),
  planConfirmationAcknowledgementsForm: select(
    'planConfirmationAcknowledgementsForm'
  ),
  erisapediaSearches: select('erisapediaSearches'),
  rolloverType: select('rolloverType'),
  addAddendum: select('addAddendum'),
  addNotificationsAddendum: select('addNotificationsAddendum'),
  planMatchResults: select('planMatchResults'),
}

selectors.transformedPlanSponsors = buildTransformedAudienceSelector(
  selectors.planSponsors,
  'planSponsorCompany',
  'planSponsorContact',
  AudienceType.PLAN_SPONSOR
)
selectors.transformedRecordkeepers = buildTransformedAudienceSelector(
  selectors.recordkeepers,
  'recordKeeperCompany',
  'recordKeeperContact',
  AudienceType.RECORD_KEEPER
)
selectors.transformedTpas = buildTransformedAudienceSelector(
  selectors.tpas,
  'tpaCompany',
  'tpaContact',
  AudienceType.TPA
)
selectors.transformedConsultants = buildTransformedAudienceSelector(
  selectors.consultants,
  'consultantCompany',
  'consultantContact',
  AudienceType.CONSULTANT
)

export { reducer, selectors, reducerKey }
